import type { NameColumnConfig } from './parsers/types';
import type { PdfExtractionSummary } from './parsers/pdfLoader';

// Structurally identical to step2Helpers.ts's TemplateEntry — duplicated here
// (rather than imported) because step2Helpers.ts already imports ColumnMapping/
// WizardState FROM this file, and importing TemplateEntry back would create a
// circular import between the two.
interface MappingsPayloadEntry {
	colIndex?: number;
	sourceHeader: string;
	sectionNo: number;
	fieldName: string;
	negate: boolean;
	skip?: boolean;
	employeeName?: boolean;
}

// Smart Import wizard state. The full four-step shape is defined now (it governs
// WS-9/10/11), but only the Step 1 fields are populated by WS-8 runtime code.
// State lives in a useReducer inside SmartImportWizard — not in Redux — because
// the wizard is short-lived, writes nothing to the store until Confirm, and no
// other component reads it (prep doc D3).

export interface WizardState {
	currentStep: 0 | 1 | 2 | 3;
	parseError: string | null;

	// Step 1
	// ENG-383: the raw File is retained (not just {name,type}) so Step 2 can
	// re-read and re-parse a different sheet when the user switches sheets.
	file: File | null;
	// Set only for a PDF upload (see parsers/pdfLoader.ts) — the full list of
	// files uploaded together in one session (length 1 for a single PDF, same
	// as `file`). Null for CSV/XLSX, and null for a PDF upload's own `file`
	// re-parse path since PDFs are never re-parsed client-side. Used by
	// Step 1/Step 2 to display "N files" instead of a single filename.
	pdfFiles: File[] | null;
	sheets: Array<{ name: string; score: number }>;
	selectedSheet: string | null;
	headerRowIndex: number;
	rawMatrix: string[][]; // 2-D raw strings; used by Step 2 auto-detection and Step 3 aggregation
	rawRows: Array<Record<string, unknown>>; // keyed by detected header; built post-detection
	// Set only when the source file was a PDF (see parsers/pdfLoader.ts) — null
	// for CSV/XLSX. Surfaced in Step 2 (excluded pages, billing period vs. pay
	// period mismatch) and Step 4 (stated total cross-check). Always the
	// multi-document summary shape now, even for one PDF (see
	// joinAcrossDocuments.ts on the backend — a single upload is the N=1 case
	// of the same code path).
	pdfExtraction: PdfExtractionSummary | null;
	// Reconciliation aid for a PDF-sourced import only (null for CSV/XLSX,
	// which carry no benefit-type signal — see priorPeriodReconciliationService.ts
	// on the backend). Keyed exactly like errorCells (`${rowId}|${fieldId}`),
	// populated once Step 3 resolves employee matches (needs both the field
	// mapping from Step 2 AND the resolved employee id from Step 3).
	priorPeriodValues: Record<string, { priorValue: number; shouldClear: boolean; fieldName: string }> | null;

	// Step 2 (WS-9 populates)
	columnMappings: Array<{
		colIndex: number; // stable identity/lookup key — sourceHeader can repeat (ADP)
		sourceHeader: string; // display only; not used as a key
		category: 'employee_name' | 'skip' | string;
		fieldRef:
			| { type: 'none' }
			| { type: 'existing'; fieldId: string }
			| { type: 'pending'; tempId: string };
		negate: boolean;
		classifier: 'numeric' | 'rate' | 'hours' | 'text' | 'skip';
		sampleValues: string[];
	}>;
	pendingFields: Array<{
		tempId: string;
		label: string;
		sectionId: string;
		derivedJsonId: string;
	}>;
	loadedTemplateId: string | null;
	// Whether Step 2's mappings were auto-filled from the company's silent
	// "last used" default (SmartImportDefaultMapping on the backend), not a
	// named template — drives the Step 2 transparency banner. Reset to false
	// whenever a fresh file/sheet starts a new Step 2 pass.
	defaultMappingApplied: boolean;
	// The current mappings, in the same (sectionNo, fieldName) shape Step 2's
	// own "Save Template" already builds (step2Helpers.ts's buildMappingsPayload)
	// — computed once when Step 2 is left (Next), carried across Step 3, and
	// silently POSTed as the company's new default mapping only after Step 4's
	// Confirm Import actually succeeds (never from an abandoned session).
	mappingsPayloadForSave: MappingsPayloadEntry[] | null;

	// Step 3 (WS-10 populates)
	employeeRows: Array<{
		rowId: string;
		csvName: string;
		normalizedName: string;
		// ENG-369: 'blank_name' is an unresolved-error status for a row whose source
		// name cell was empty (csvName === ''). Like a 'review' row it blocks advance
		// to Step 4, but it can only be cleared by mapping the row to a real employee,
		// adding it as new with a typed name, or Skipping it — it is never a valid
		// import candidate on its own. Kept as a matchStatus (not a `skipped`-style
		// overlay) because it IS the row's resolution state, not an overlay on top of one.
		matchStatus: 'matched' | 'saved' | 'review' | 'new' | 'blank_name';
		matchScore: number | null;
		matchedEmployeeId: string | null;
		// ENG-410: the matched CAP employee's display name (fullName), captured at
		// resolution time so Step 4's preview can show it instead of the raw file
		// name. Null for add-as-new rows (their name is addAsNewName). Display-only —
		// not part of the import payload.
		matchedEmployeeName: string | null;
		addAsNewName: string | null;
		// True when the matched employee is inactive in the app. Persisted on the
		// row (the roster needed to derive it lives only in Step 3) so Step 4's
		// payload builder can auto-collect reactivations without the roster.
		inactive: boolean;
		fuzzyScore: number | null;
		// ENG-414: the user marked this row Skip in Step 3's "Maps to" dropdown —
		// it is excluded from the import entirely (no cost row, no new employee, no
		// reactivation, no saved alias) and hidden from Step 4's preview.
		//
		// Deliberately an OVERLAY rather than a matchStatus member: the underlying
		// resolution (matchStatus / matchedEmployeeId / addAsNewName) is left intact
		// underneath, so un-skipping restores the row's prior mapping — including a
		// 'saved' alias match — with no re-confirmation and nothing to re-derive.
		skipped: boolean;
		// P2.2: worst-of confidence across this employee's contributing PDF source
		// rows ('low' if any was low). Null for CSV/XLSX imports, which carry no
		// AI confidence at all. Display-only — not part of the import payload.
		confidence: 'high' | 'low' | null;
	}>;
	aggregatedRows: Array<{
		rowId: string;
		csvName: string;
		values: Record<string, number>;
		// P2.2: see employeeRows.confidence — carried through from here so
		// computeEmployeeRows can copy it onto the row it derives.
		confidence: 'high' | 'low' | null;
	}>;

	// ENG-414: whether the one-time "skipping rows" advisory toast has fired this
	// session. Lives in the reducer, not Step 3 local state, because Step 3 is
	// conditionally mounted (SmartImportWizard renders it only while currentStep
	// === 2) — local state would reset on Back from Step 4 and re-fire the toast.
	skipToastShown: boolean;

	// Step 4 (WS-11 populates)
	payPeriodId: string | null;
	totals: { fileTotal: number; importTotal: number };
	warnings: string[];
}

export const initialState: WizardState = {
	currentStep: 0,
	parseError: null,
	file: null,
	pdfFiles: null,
	sheets: [],
	selectedSheet: null,
	headerRowIndex: 0,
	rawMatrix: [],
	rawRows: [],
	pdfExtraction: null,
	priorPeriodValues: null,
	columnMappings: [],
	pendingFields: [],
	loadedTemplateId: null,
	defaultMappingApplied: false,
	mappingsPayloadForSave: null,
	employeeRows: [],
	aggregatedRows: [],
	skipToastShown: false,
	payPeriodId: null,
	totals: { fileTotal: 0, importTotal: 0 },
	warnings: [],
};

export type WizardAction =
	| { type: 'FILE_SELECTED'; file: WizardState['file'] }
	// PDF upload path only (Step1Upload's handlePdf) — mirrors FILE_SELECTED's
	// parse-state-clearing behavior but also records the full file list (one
	// or several PDFs uploaded together) for Step 1/Step 2 display.
	| { type: 'PDF_FILES_SELECTED'; files: File[] }
	| {
			type: 'PARSE_SUCCEEDED';
			payload: {
				sheets: WizardState['sheets'];
				selectedSheet: string | null;
				headerRowIndex: number;
				rawMatrix: string[][];
				rawRows: WizardState['rawRows'];
				// Required (not optional) so every PARSE_SUCCEEDED dispatch must decide
				// this explicitly — null for CSV/XLSX — rather than risking a PDF's
				// metadata silently surviving spread-over into a later CSV/XLSX parse.
				pdfExtraction: PdfExtractionSummary | null;
			};
	  }
	| { type: 'SHEET_SWITCHED'; sheet: string }
	| { type: 'PARSE_FAILED'; error: string }
	// Step 2 (WS-9). Column-mapping decisions are keyed by sourceHeader.
	| { type: 'STEP2_INITIALIZED'; columnMappings: WizardState['columnMappings'] }
	// ENG-408: on-demand keyword suggestion — replaces columnMappings wholesale,
	// unguarded (STEP2_INITIALIZED is one-shot and would no-op here).
	| { type: 'CATEGORIES_SUGGESTED'; columnMappings: WizardState['columnMappings'] }
	| { type: 'COLUMN_CATEGORY_CHANGED'; colIndex: number; category: string }
	| {
			type: 'COLUMN_FIELD_CHANGED';
			colIndex: number;
			fieldRef: ColumnMapping['fieldRef'];
	  }
	| { type: 'COLUMN_NEGATE_TOGGLED'; colIndex: number }
	| {
			type: 'PENDING_FIELD_ADDED';
			pendingField: WizardState['pendingFields'][number];
			colIndex: number;
	  }
	| { type: 'PENDING_FIELD_REMOVED'; tempId: string }
	| {
			type: 'TEMPLATE_APPLIED';
			columnMappings: WizardState['columnMappings'];
			loadedTemplateId: string;
	  }
	| { type: 'TEMPLATE_LOADED_ID_SET'; loadedTemplateId: string | null }
	// The company's silent "last used" mapping (not a named template) was
	// auto-applied on Step 2 entry — see applyDefaultMappingIfGoodMatch in
	// Step2MapColumns.tsx. Deliberately does NOT set loadedTemplateId: this
	// isn't a saved template, so "Load template..." must not show it selected
	// and Save must still behave as "no template loaded" (Save As only).
	| { type: 'DEFAULT_MAPPING_APPLIED'; columnMappings: WizardState['columnMappings'] }
	// Snapshot of the current mappings in save-payload shape, captured when
	// Step 2 is left — see WizardState.mappingsPayloadForSave.
	| { type: 'MAPPINGS_PAYLOAD_FOR_SAVE_SET'; payload: WizardState['mappingsPayloadForSave'] }
	// Step 3 (WS-10). employeeRows are keyed by rowId.
	| {
			type: 'STEP3_INITIALIZED';
			payload: {
				aggregatedRows: WizardState['aggregatedRows'];
				employeeRows: WizardState['employeeRows'];
			};
	  }
	// Reconciliation aid — fired once Step 3 has resolved employee matches for
	// a PDF-sourced import (see Step3MapEmployees.tsx's fetch effect).
	| { type: 'PRIOR_PERIOD_VALUES_LOADED'; values: WizardState['priorPeriodValues'] }
	| {
			type: 'EMPLOYEE_MATCH_OVERRIDDEN';
			payload: {
				rowId: string;
				matchedEmployeeId: string;
				// ENG-410: display name (fullName) of the chosen employee, for Step 4.
				matchedEmployeeName: string | null;
				matchStatus: 'matched' | 'review' | 'saved';
				inactive: boolean;
			};
	  }
	// Revert a row to the add-as-new state (the "Add as new employee" override).
	| { type: 'EMPLOYEE_SET_ADD_AS_NEW'; payload: { rowId: string } }
	// Set the typed name for an add-as-new row (the inline name-edit input).
	| {
			type: 'EMPLOYEE_ADD_AS_NEW_NAME_SET';
			payload: { rowId: string; addAsNewName: string };
	  }
	// ENG-414: mark/unmark a row as Skip. Purely an overlay — the row's underlying
	// resolution is untouched, so un-skipping restores it as it was.
	| { type: 'EMPLOYEE_SKIP_SET'; payload: { rowId: string; skipped: boolean } }
	// ENG-414: the one-time skip advisory toast has been shown this session.
	| { type: 'SKIP_TOAST_SHOWN' }
	| { type: 'ADVANCE_STEP' }
	| { type: 'BACK_STEP' }
	// Step 4 (WS-11). Direct jump used by the "Fix in Step 2/3" error links —
	// existing step state is preserved (Steps 2/3 init guards prevent clobber).
	| { type: 'GOTO_STEP'; step: number }
	| { type: 'RESET' };

// A single row of the Step 2 column-mapping table.
export type ColumnMapping = WizardState['columnMappings'][number];

// Re-exported for components that need the detected name layout type.
export type { NameColumnConfig };

const clearedParseState = {
	parseError: null,
	sheets: [] as WizardState['sheets'],
	selectedSheet: null,
	headerRowIndex: 0,
	rawMatrix: [] as string[][],
	rawRows: [] as WizardState['rawRows'],
	pdfExtraction: null as WizardState['pdfExtraction'],
	priorPeriodValues: null as WizardState['priorPeriodValues'],
};

// Navigating back to Step 2 (or earlier) invalidates Step 3's aggregation: the
// user may change column mappings, so the cached aggregatedRows/employeeRows are
// stale. Clearing them makes Step 3's `employeeRows.length === 0` init guard fire
// again on the next advance, re-aggregating from the current mappings. Returning to
// Step 3 from Step 4 (currentStep 2) preserves the data — no mappings can change there.
function step3ResetFor(
	currentStep: WizardState['currentStep']
): Partial<WizardState> {
	// ENG-414: `skipped` flags live on employeeRows, so they are discarded with
	// them; skipToastShown resets too, so a re-aggregated set of rows is treated as
	// a fresh session and the advisory toast can fire again on the next first Skip.
	return currentStep < 2
		? { aggregatedRows: [], employeeRows: [], skipToastShown: false, priorPeriodValues: null }
		: {};
}

export function wizardReducer(
	state: WizardState,
	action: WizardAction
): WizardState {
	switch (action.type) {
		case 'FILE_SELECTED':
			return {
				...state,
				file: action.file,
				pdfFiles: null,
				...clearedParseState,
				// A new file invalidates any Step 2 work from a prior file (after
				// "Change File"); start the next mapping pass fresh.
				columnMappings: [],
				pendingFields: [],
				defaultMappingApplied: false,
				mappingsPayloadForSave: null,
				// ENG-372 (Option B): loadedTemplateId is deliberately PRESERVED across a
				// file change so the template can be auto-re-applied on Step 2 re-entry
				// (the ticket's specified behavior). The parsed headers aren't on this
				// action — parsing is async and they arrive later via PARSE_SUCCEEDED — so
				// the "does the new file still match this template?" decision can't be made
				// here; it is deferred to Step 2 mount, which re-applies the template when
				// the new file shares columns with it, or clears loadedTemplateId when it
				// doesn't. (Replaces the earlier unconditional `loadedTemplateId: null`.)
			};
		case 'PDF_FILES_SELECTED':
			return {
				...state,
				file: action.files[0] ?? null,
				pdfFiles: action.files,
				...clearedParseState,
				columnMappings: [],
				pendingFields: [],
				defaultMappingApplied: false,
				mappingsPayloadForSave: null,
				// See FILE_SELECTED's comment — same ENG-372 (Option B) reasoning.
			};
		case 'PARSE_SUCCEEDED':
			return {
				...state,
				parseError: null,
				...action.payload,
			};
		case 'SHEET_SWITCHED':
			// ENG-383: switching sheets invalidates everything derived from the old
			// sheet. Reset the parse + Step 2 state exactly like FILE_SELECTED, then
			// the async re-parse dispatches PARSE_SUCCEEDED for the chosen sheet.
			// `sheets` is intentionally preserved — the switcher list belongs to the
			// same workbook (and PARSE_SUCCEEDED refreshes it anyway), so keeping it
			// avoids the dropdown flickering out mid-switch.
			return {
				...state,
				parseError: null,
				selectedSheet: action.sheet,
				headerRowIndex: 0,
				rawMatrix: [],
				rawRows: [],
				columnMappings: [],
				pendingFields: [],
				defaultMappingApplied: false,
				mappingsPayloadForSave: null,
				// ENG-372 (Option B): as with FILE_SELECTED, preserve loadedTemplateId so
				// Step 2 can re-apply the template against the newly-parsed sheet (or clear
				// it when the sheet shares no columns). The switched sheet's headers aren't
				// on this action either — the async re-parse dispatches them via
				// PARSE_SUCCEEDED — so the match decision is deferred to handleSheetSwitch.
				...step3ResetFor(state.currentStep),
			};
		case 'PARSE_FAILED':
			return {
				...state,
				...clearedParseState,
				parseError: action.error,
			};
		case 'STEP2_INITIALIZED':
			// Guard against re-entry clobber (Back then Next): only seed the
			// auto-detected mappings when the user has none yet (prep D2 / R-4).
			if (state.columnMappings.length > 0) return state;
			return { ...state, columnMappings: action.columnMappings };
		case 'CATEGORIES_SUGGESTED':
			// ENG-408: straight replace, no guard — the user explicitly asked to
			// re-categorize existing mappings via the Suggest Categories button.
			return { ...state, columnMappings: action.columnMappings };
		case 'COLUMN_CATEGORY_CHANGED':
			return {
				...state,
				columnMappings: state.columnMappings.map((m): ColumnMapping => {
					if (m.colIndex !== action.colIndex) return m;
					const isSentinel =
						action.category === 'employee_name' || action.category === 'skip';
					return {
						...m,
						category: action.category,
						// Changing section (or moving to a sentinel) invalidates the
						// chosen field; reset it. Sentinels also clear the toggle.
						fieldRef: { type: 'none' },
						negate: isSentinel ? false : m.negate,
					};
				}),
			};
		case 'COLUMN_FIELD_CHANGED':
			return {
				...state,
				columnMappings: state.columnMappings.map(
					(m): ColumnMapping =>
						m.colIndex === action.colIndex
							? { ...m, fieldRef: action.fieldRef }
							: m
				),
			};
		case 'COLUMN_NEGATE_TOGGLED':
			return {
				...state,
				columnMappings: state.columnMappings.map(
					(m): ColumnMapping =>
						m.colIndex === action.colIndex
							? { ...m, negate: !m.negate }
							: m
				),
			};
		case 'PENDING_FIELD_ADDED':
			return {
				...state,
				pendingFields: [...state.pendingFields, action.pendingField],
				columnMappings: state.columnMappings.map(
					(m): ColumnMapping =>
						m.colIndex === action.colIndex
							? {
									...m,
									fieldRef: {
										type: 'pending',
										tempId: action.pendingField.tempId,
									},
							  }
							: m
				),
			};
		case 'PENDING_FIELD_REMOVED':
			return {
				...state,
				pendingFields: state.pendingFields.filter(
					(pf) => pf.tempId !== action.tempId
				),
				// Any column still pointing at the removed pending field falls back
				// to unmapped so it can't dangle into the Confirm payload.
				columnMappings: state.columnMappings.map(
					(m): ColumnMapping =>
						m.fieldRef.type === 'pending' && m.fieldRef.tempId === action.tempId
							? { ...m, fieldRef: { type: 'none' } }
							: m
				),
			};
		case 'TEMPLATE_APPLIED':
			return {
				...state,
				columnMappings: action.columnMappings,
				loadedTemplateId: action.loadedTemplateId,
			};
		case 'TEMPLATE_LOADED_ID_SET':
			return { ...state, loadedTemplateId: action.loadedTemplateId };
		case 'DEFAULT_MAPPING_APPLIED':
			return {
				...state,
				columnMappings: action.columnMappings,
				defaultMappingApplied: true,
			};
		case 'MAPPINGS_PAYLOAD_FOR_SAVE_SET':
			return { ...state, mappingsPayloadForSave: action.payload };
		case 'PRIOR_PERIOD_VALUES_LOADED':
			return { ...state, priorPeriodValues: action.values };
		case 'STEP3_INITIALIZED':
			// Guard against re-entry clobber (Back then Next): only seed the
			// computed rows when Step 3 has none yet, preserving user resolutions.
			if (state.employeeRows.length > 0) return state;
			return {
				...state,
				aggregatedRows: action.payload.aggregatedRows,
				employeeRows: action.payload.employeeRows,
			};
		case 'EMPLOYEE_MATCH_OVERRIDDEN':
			return {
				...state,
				employeeRows: state.employeeRows.map((r) =>
					r.rowId === action.payload.rowId
						? {
								...r,
								matchedEmployeeId: action.payload.matchedEmployeeId,
								matchedEmployeeName: action.payload.matchedEmployeeName,
								matchStatus: action.payload.matchStatus,
								inactive: action.payload.inactive,
								// A deliberate override clears any pending add-as-new name.
								addAsNewName: null,
								// ENG-414: picking a real employee is a mapping decision, so it
								// also lifts Skip — a row can't be both skipped and mapped.
								skipped: false,
						  }
						: r
				),
			};
		case 'EMPLOYEE_SET_ADD_AS_NEW':
			// Full revert to the new-band default: status 'new', no matched employee,
			// add-as-new name mirrors the source name, inactive flag cleared.
			return {
				...state,
				employeeRows: state.employeeRows.map((r) =>
					r.rowId === action.payload.rowId
						? {
								...r,
								matchStatus: 'new',
								matchedEmployeeId: null,
								// ENG-410: new rows show addAsNewName, not a matched name.
								matchedEmployeeName: null,
								addAsNewName: r.csvName,
								matchScore: null,
								inactive: false,
								// ENG-414: as with an override, choosing add-as-new lifts Skip.
								skipped: false,
						  }
						: r
				),
			};
		case 'EMPLOYEE_ADD_AS_NEW_NAME_SET':
			// The inline name-edit committed a new name; the row stays add-as-new.
			return {
				...state,
				employeeRows: state.employeeRows.map((r) =>
					r.rowId === action.payload.rowId
						? { ...r, addAsNewName: action.payload.addAsNewName }
						: r
				),
			};
		case 'EMPLOYEE_SKIP_SET':
			// Only the overlay flips. Everything else on the row (matchStatus,
			// matchedEmployeeId, matchedEmployeeName, addAsNewName, inactive) is
			// preserved so un-skip restores the prior mapping verbatim.
			return {
				...state,
				employeeRows: state.employeeRows.map((r) =>
					r.rowId === action.payload.rowId
						? { ...r, skipped: action.payload.skipped }
						: r
				),
			};
		case 'SKIP_TOAST_SHOWN':
			return { ...state, skipToastShown: true };
		case 'ADVANCE_STEP':
			return {
				...state,
				currentStep: Math.min(3, state.currentStep + 1) as WizardState['currentStep'],
			};
		case 'BACK_STEP': {
			const currentStep = Math.max(
				0,
				state.currentStep - 1
			) as WizardState['currentStep'];
			return { ...state, currentStep, ...step3ResetFor(currentStep) };
		}
		case 'GOTO_STEP': {
			const currentStep = Math.max(
				0,
				Math.min(3, action.step)
			) as WizardState['currentStep'];
			return { ...state, currentStep, ...step3ResetFor(currentStep) };
		}
		case 'RESET':
			return initialState;
		default:
			return state;
	}
}
